import React from 'react';
import { IncrementRecordRowDefinition } from 'Components/Worksheets/PlateLoadingWorksheet/types';
import { Field, NumericInput, TextOutput } from 'Components/Core';
import { Grid } from 'Components/Layout';
import { withInjectedProps } from 'Components/Core/InjectedPropsContext';
import { WorksheetGridFieldSection } from 'Components/Worksheets/Core';

interface InterfaceIncrementReadingsGridProps {
  readings: IncrementRecordRowDefinition[];
  readingName: (index?: number) => string;
  name: string;
  onCreateRow?: () => void;
  onRemoveRow?: (row: number) => void;
  initialSeatingReading?: boolean;
}

let IncrementReadingsGridSectionBase: React.FC<InterfaceIncrementReadingsGridProps> = ({
  readings,
  readingName,
  name,
  onCreateRow,
  onRemoveRow,
  initialSeatingReading,
}) => {
  type ColumnDefinition = {
    header: string;
    width: number;
    getFieldForRecord: (rrd: IncrementRecordRowDefinition, i: Number) => React.ReactElement;
    key?: string;
  };

  const gridColumns: ColumnDefinition[] = [
    {
      header: 'Elapsed Time (min)',
      width: 110,
      getFieldForRecord: (r: IncrementRecordRowDefinition, i: Number) => (
        <Field input={<NumericInput />} name={`${readingName(r.index)}.ElapsedTime`} disabled={i === 0} />
      ),
      key: 'elapsedTime',
    },
    {
      header: 'Reading 1 (divs)',
      width: 110,
      getFieldForRecord: (r: IncrementRecordRowDefinition) => (
        <Field input={<NumericInput />} name={`${readingName(r.index)}.GaugeReading1`} />
      ),
      key: 'GaugeReading1',
    },
    {
      header: 'Reading 2 (divs)',
      width: 110,
      getFieldForRecord: (r: IncrementRecordRowDefinition) => (
        <Field input={<NumericInput />} name={`${readingName(r.index)}.GaugeReading2`} />
      ),
      key: 'GaugeReading2',
    },
    {
      header: 'Reading 3 (divs)',
      width: 110,
      getFieldForRecord: (r: IncrementRecordRowDefinition) => (
        <Field input={<NumericInput />} name={`${readingName(r.index)}.GaugeReading3`} />
      ),
      key: 'GaugeReading3',
    },
    {
      header: initialSeatingReading ? 'Average Reading (mm)' : 'Average Settlement (mm)',
      width: 110,
      getFieldForRecord: (r: IncrementRecordRowDefinition) => (
        <Field
          input={<TextOutput />}
          name={`${readingName(r.index)}${initialSeatingReading ? '.AverageReading' : '.AverageSettlement'}`}
        />
      ),
      key: 'averageValue',
    },
  ];

  type RowDefinition = {
    data: (React.ReactElement<any> | undefined)[];
    preventRemove: boolean;
    onRemove?: () => any;
    key: string;
  };

  const gridRows: RowDefinition[] = [
    ...(readings?.map((r, i) => ({
      // Row for each reading
      data: gridColumns.map((c) => c.getFieldForRecord(r, i)),
      preventRemove: i <= 1,
      key: `readingRow_${i}`,
    })) ?? []),
  ];

  return (
    <WorksheetGridFieldSection name={name}>
      <Grid
        columns={gridColumns.map((c) => ({ key: c.key }))}
        colHeaders={gridColumns.map((c) => c.header)}
        colWidths={gridColumns.map((c) => c.width)}
        hiddenCols={initialSeatingReading ? [0] : []}
        rowHeaderWidth={140}
        data={gridRows.map((r) => r.data)}
        onCreateRow={onCreateRow}
        onRemoveRow={(i: number) => onRemoveRow?.(i)}
        rows={gridRows.map((r) => ({ preventRemoveRow: r.preventRemove, key: r.key }))}
        removeConfirm={{
          confirm: true,
          confirmTitle: 'Load Increment Reading Deletion',
          confirmText: 'Are you sure you want to delete the selected reading?',
          confirmButtonText: 'Delete Reading',
        }}
        tabDirection="horizontal"
      />
    </WorksheetGridFieldSection>
  );
};

const IncrementReadingsGridSection = withInjectedProps(IncrementReadingsGridSectionBase);

export { IncrementReadingsGridSection };
export default IncrementReadingsGridSection;
